/**
 * Agent workflow editor (React Flow) + metadata. Routes: /agent-workflow/new, /agent-workflow/:id
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuth } from '../contexts/AuthContext';
import { AgentWorkflowCanvas } from '../components/agent-workflow/AgentWorkflowCanvas';
import {
  draftGraphToRecord,
  parseDraftGraph,
  type AgentWorkflowDraftGraph,
} from '../components/agent-workflow/graphMapper';
import {
  hubService,
  type AgentWorkflow,
  type CompileAgentWorkflowResult,
  type AgentWorkflowEdgeTypeDef,
  type AgentWorkflowNodeTypeDef,
} from '../services/hub.service';

const DEFAULT_SYSTEM = 'chatbot';
const DEFAULT_FLOW_TYPE = 'agent';

export function AgentWorkflowDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const isNew = !id || id === 'new';

  const [workflow, setWorkflow] = useState<AgentWorkflow | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [system, setSystem] = useState(DEFAULT_SYSTEM);
  const [botId, setBotId] = useState('default');
  const [enabled, setEnabled] = useState(true);

  const [draftGraphRecord, setDraftGraphRecord] = useState<Record<string, unknown>>({});
  const [graphSyncKey, setGraphSyncKey] = useState('init');
  const [nodeTypes, setNodeTypes] = useState<AgentWorkflowNodeTypeDef[]>([]);
  const [edgeTypes, setEdgeTypes] = useState<AgentWorkflowEdgeTypeDef[]>([]);
  const [compileResult, setCompileResult] = useState<CompileAgentWorkflowResult | null>(null);
  const [compileError, setCompileError] = useState<string | null>(null);
  const [testInput, setTestInput] = useState('');
  const [testResponse, setTestResponse] = useState<string | null>(null);
  const [testTrace, setTestTrace] = useState<string | null>(null);
  const [runningTest, setRunningTest] = useState(false);

  const onDraftGraphChange = useCallback((g: AgentWorkflowDraftGraph) => {
    setDraftGraphRecord(draftGraphToRecord(g));
  }, []);

  useEffect(() => {
    if (isNew) {
      setLoading(false);
      setWorkflow(null);
      setDraftGraphRecord({});
      setGraphSyncKey(`new-${Date.now()}`);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const token = getToken();
        const w = await hubService.getAgentWorkflow(id!, token);
        if (cancelled) return;
        setWorkflow(w);
        setName(w.name);
        setDescription(w.description ?? '');
        setSystem(w.system);
        setBotId(w.botId);
        setEnabled(w.enabled);
        const dg =
          w.draftGraph && typeof w.draftGraph === 'object' ? (w.draftGraph as Record<string, unknown>) : {};
        setDraftGraphRecord(dg);
        setGraphSyncKey(`${w.id}-${w.draftVersion}-${Date.now()}`);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load workflow');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, isNew, getToken]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = getToken();
        const [list, edgeList] = await Promise.all([
          hubService.listAgentWorkflowNodeTypes('agent', token),
          hubService.listAgentWorkflowEdgeTypes('agent', token),
        ]);
        if (!cancelled) {
          setNodeTypes(Array.isArray(list) ? list : []);
          setEdgeTypes(Array.isArray(edgeList) ? edgeList : []);
        }
      } catch {
        if (!cancelled) {
          setNodeTypes([]);
          setEdgeTypes([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  const versionChips = useMemo(() => {
    if (!workflow) return null;
    return (
      <div className="flex flex-wrap gap-2 text-xs">
        <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200">
          Draft v{workflow.draftVersion}
        </span>
        <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
          Published v{workflow.publishedVersion}
        </span>
      </div>
    );
  }, [workflow]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = getToken();
      if (isNew) {
        if (!name.trim() || !botId.trim()) {
          setError('Name and bot ID are required');
          setSaving(false);
          return;
        }
        const created = await hubService.createAgentWorkflow(
          {
            system: system.trim() || DEFAULT_SYSTEM,
            botId: botId.trim(),
            name: name.trim(),
            description: description.trim() || undefined,
            flowType: DEFAULT_FLOW_TYPE,
            draftGraph: draftGraphRecord,
          },
          token,
        );
        navigate(`/agent-workflow/${created.id}`, { replace: true });
        return;
      }
      const updated = await hubService.updateAgentWorkflow(
        id!,
        {
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          flowType: DEFAULT_FLOW_TYPE,
          draftGraph: draftGraphRecord,
          enabled,
        },
        token,
      );
      setWorkflow(updated);
      setGraphSyncKey(`${updated.id}-${updated.draftVersion}-${Date.now()}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (isNew || !id) {
      setError('Save the workflow before publishing');
      return;
    }
    setPublishing(true);
    setError(null);
    try {
      const token = getToken();
      await hubService.updateAgentWorkflow(
        id,
        {
          name: name.trim() || undefined,
          description: description.trim() || undefined,
          flowType: DEFAULT_FLOW_TYPE,
          draftGraph: draftGraphRecord,
          enabled,
        },
        token,
      );
      const published = await hubService.publishAgentWorkflow(id, {}, token);
      setWorkflow(published);
      setGraphSyncKey(`${published.id}-${published.draftVersion}-${Date.now()}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const handleCompile = async () => {
    if (isNew || !id) return;
    setCompileError(null);
    try {
      const token = getToken();
      const compiled = await hubService.compileAgentWorkflow(id, token);
      setCompileResult(compiled);
    } catch (e) {
      setCompileResult(null);
      setCompileError(e instanceof Error ? e.message : 'Compile failed');
    }
  };

  const handleRuntimeTest = async () => {
    if (!testInput.trim()) return;
    setRunningTest(true);
    setTestResponse(null);
    setTestTrace(null);
    try {
      const token = getToken();
      const result = await hubService.agentRuntimeRoute(
        {
          message: testInput.trim(),
          system: system.trim() || DEFAULT_SYSTEM,
          botId: botId.trim(),
          userId: 'workflow-playground',
          channel: 'web',
          pageId: 'builder',
        },
        token,
      );
      setTestResponse(result.response ?? '');
      setTestTrace(result.trace ? JSON.stringify(result.trace, null, 2) : null);
    } catch (e) {
      setTestResponse(`Error: ${e instanceof Error ? e.message : 'Runtime test failed'}`);
      setTestTrace(null);
    } finally {
      setRunningTest(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full w-full flex flex-col items-center justify-center gap-3 bg-gray-50">
        <div
          className="h-10 w-10 rounded-full border-2 border-gray-200 border-t-blue-500 animate-spin"
          aria-hidden
        />
        <p className="text-sm text-gray-500">Loading workflow…</p>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col bg-gray-50 overflow-hidden">
      <header className="flex-shrink-0 min-h-14 px-4 py-2 flex flex-wrap items-center justify-between gap-3 bg-white border-b border-gray-200">
        <div className="flex items-center gap-3 min-w-0">
          <Link to="/agent-workflow" className="flex-shrink-0 text-gray-500 hover:text-gray-800" title="Back">
            <span className="text-lg" aria-hidden>
              ←
            </span>
          </Link>
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {isNew ? 'New agent workflow' : name || 'Workflow'}
            </h1>
            {versionChips}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {error ? (
            <p className="text-sm text-red-600 max-w-[220px] truncate" title={error}>
              {error}
            </p>
          ) : null}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || publishing || !name.trim()}
            className="px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving…' : isNew ? 'Create' : 'Save draft'}
          </button>
          {!isNew ? (
            <button
              type="button"
              onClick={handleCompile}
              disabled={saving || publishing}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Compile
            </button>
          ) : null}
          {!isNew ? (
            <button
              type="button"
              onClick={handlePublish}
              disabled={saving || publishing}
              className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              {publishing ? 'Publishing…' : 'Publish'}
            </button>
          ) : null}
        </div>
      </header>

      <div className="flex-shrink-0 border-b border-gray-200 bg-white px-4 py-3">
        <div className="max-w-6xl flex flex-wrap items-end gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm w-56"
              placeholder="Workflow name"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Description</label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm w-64"
              placeholder="optional"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">System</label>
            <input
              type="text"
              value={system}
              onChange={(e) => setSystem(e.target.value)}
              disabled={!isNew && !!workflow}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm w-32 disabled:bg-gray-100"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-0.5">Bot ID</label>
            <input
              type="text"
              value={botId}
              onChange={(e) => setBotId(e.target.value)}
              disabled={!isNew && !!workflow}
              className="rounded-md border border-gray-300 px-2 py-1.5 text-sm w-40 disabled:bg-gray-100"
            />
          </div>
          {!isNew ? (
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">Enabled</span>
            </label>
          ) : null}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col bg-white">
        <AgentWorkflowCanvas
          draftGraph={draftGraphRecord}
          graphSyncKey={graphSyncKey}
          onDraftGraphChange={onDraftGraphChange}
          availableNodeTypes={nodeTypes}
          availableEdgeTypes={edgeTypes}
        />
        <p className="text-[11px] text-gray-400 px-4 py-1 border-t border-gray-100">
          Nodes: {parseDraftGraph(draftGraphRecord).nodes.length} · Edges:{' '}
          {parseDraftGraph(draftGraphRecord).edges.length}
        </p>
        {!isNew ? (
          <div className="border-t border-gray-200 p-4 grid grid-cols-1 lg:grid-cols-2 gap-4 bg-gray-50">
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-sm font-semibold text-gray-800">Publish validation report</p>
              {compileError ? <p className="mt-2 text-xs text-red-600">{compileError}</p> : null}
              {compileResult ? (
                <p className="mt-2 text-xs text-gray-600">
                  valid={String(compileResult.report.valid)} · nodes={compileResult.report.nodeCount} · edges=
                  {compileResult.report.edgeCount} · entry={compileResult.report.entryNodeId ?? '-'}
                </p>
              ) : (
                <p className="mt-2 text-xs text-gray-500">Run Compile to preview publish-time validation.</p>
              )}
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-sm font-semibold text-gray-800">Runtime test playground</p>
              <textarea
                value={testInput}
                onChange={(e) => setTestInput(e.target.value)}
                rows={3}
                className="mt-2 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                placeholder="Type a user message to test route + execute"
              />
              <button
                type="button"
                onClick={handleRuntimeTest}
                disabled={runningTest || !testInput.trim()}
                className="mt-2 px-3 py-1.5 text-xs font-medium rounded border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {runningTest ? 'Running…' : 'Run test'}
              </button>
              {testResponse ? <p className="mt-2 text-xs text-gray-700 whitespace-pre-wrap">{testResponse}</p> : null}
              {testTrace ? (
                <pre className="mt-2 text-[11px] bg-gray-900 text-gray-100 p-2 rounded overflow-x-auto">{testTrace}</pre>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
